import { createApp } from 'vue';
import { registerSW } from 'virtual:pwa-register';
import App from './App.vue';
import router from './router/index';
import pinia from './stores/index';
import axiosPlugin from './boot/axios';
import firebasePlugin from './boot/firebase';
import { escapeInAppBrowserOnBoot } from './utils/inAppBrowser';
// Import custom styles
import './css/app.scss';

declare const __APP_BUILD_TIME__: string;

// Boot-level safety net: detect stale cached bundle BEFORE Vue mounts.
// useVersionCheck runs onMounted, so it never fires if mount itself fails.
async function preMountVersionCheck(): Promise<void> {
  if (import.meta.env.DEV) return;
  if (sessionStorage.getItem('preMountVersionCheckRan') === '1') return;
  sessionStorage.setItem('preMountVersionCheckRan', '1');

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) return;
    const version = (await response.json()) as { buildTime?: string };
    const embedded = typeof __APP_BUILD_TIME__ !== 'undefined' ? __APP_BUILD_TIME__ : null;
    if (!embedded || !version.buildTime || embedded === version.buildTime) return;

    console.warn(
      '[main] Stale bundle pre-mount. Embedded:',
      embedded,
      'Server:',
      version.buildTime,
    );

    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        // If a new SW is already waiting, ACTIVATE it (skipWaiting) rather than
        // unregister — unregistering aborts the install and traps the user on
        // stale code. Only unregister when there's nothing pending.
        if (reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        } else {
          await reg.unregister();
        }
      }
    }
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((n) => caches.delete(n)));

    sessionStorage.removeItem('preMountVersionCheckRan');
    window.location.href = window.location.href.split('?')[0] + '?_=' + Date.now();
    // Never resolve so bootApp() does not run before navigation completes
    await new Promise<never>(() => {});
  } catch (error) {
    console.warn('[main] Pre-mount version check skipped:', error);
  }
}

function bootApp(): void {
  const app = createApp(App);

  app.use(pinia);
  app.use(router);
  app.use(axiosPlugin);
  app.use(firebasePlugin);

  app.mount('#app');

  registerSW();
}

// In-app browsers (LINE / FB / IG / TikTok): hand the page to Safari / Chrome
// before auth or routing, on every page — Google refuses sign-in inside them,
// and a session already inside LINE would otherwise keep the person there.
// Boot always continues: the app's own window may stay behind, and the router
// shows it the 「請改用瀏覽器開啟」 card.
escapeInAppBrowserOnBoot();
void preMountVersionCheck().then(bootApp);
